import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDataContext } from '../../../context/data-context/use-data-context';
import { AddressForm, AddressTypeCode, AddressTypes } from '../../../global-types/addresses';
import useAddressSelection from '../../../hooks/use-address-selection';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { getFieldsFromAddressResponse } from '../../../utils/address';
import { getDefaultAddressOptions } from '../utils/get-default-address-options';
import { useGetProjectCurrentEnvelope } from './use-get-project-current-envelope';

export const useAddressForm = () => {
  const { getProjectCurrentEnvelopeType } = useGetProjectCurrentEnvelope();
  const isOneToMany = useIsOneToMany();

  // Contexts
  const { initializedDataState } = useDataContext();
  const { isRecipientAddress: isRecipientAddress1toM } = useAddressSelection();

  // hooks
  const { IS_ADDRESS_VALIDATION_ENABLED, IS_ENVELOPE_PRINTING_OPTIONS_ENABLED, IS_MULT_ADDRESS_ENABLED } =
    useFeatureFlags();

  const {
    addressData,
    isUK,
    isRecipientAddress: isRecipientAddress1to1,
  } = useMemo(() => initializedDataState, [initializedDataState]);

  // determine if the address is for the recipient or sender
  // based on the feature flags and the card type (1:1 or 1:M)
  const isRecipientAddress = useMemo(
    () => (isOneToMany && IS_MULT_ADDRESS_ENABLED ? isRecipientAddress1toM : isRecipientAddress1to1),
    [isOneToMany, isRecipientAddress1to1, isRecipientAddress1toM],
  );

  // Set the default address values to pre-populate the address form (if an address had been stored to the project)
  // Sets the country_code, address_type_code, and skip_usps_validation to the corresponding values
  const loadAddressFormData = () => {
    const currentAddressType = isRecipientAddress ? AddressTypes.RECIPIENT : AddressTypes.SENDER;
    const storedAddress = getFieldsFromAddressResponse(addressData[`${currentAddressType}`]);

    // 1:M card
    if (isOneToMany && IS_ENVELOPE_PRINTING_OPTIONS_ENABLED) {
      return {
        country_code: 'USA',
        skip_usps_validation: !IS_ADDRESS_VALIDATION_ENABLED,
        envelope: getProjectCurrentEnvelopeType(),
        address_type_code: isRecipientAddress ? AddressTypeCode.RECIPIENT : AddressTypeCode.SENDER,
      } as AddressForm;
    }

    // 1:1 card
    const defaultAddressOptions = getDefaultAddressOptions({
      isUK,
      selectedEnvelope: getProjectCurrentEnvelopeType(),
      currentAddressType,
      skipUspsValidation: !IS_ADDRESS_VALIDATION_ENABLED,
    });

    if (!storedAddress) {
      return {
        address_id: '',
        first_name: '',
        last_name: '',
        address_line_1: '',
        address_line_2: '',
        company_name: '',
        city: '',
        state_code: '',
        zip: '',
        ...defaultAddressOptions,
      } as AddressForm;
    }

    return { ...storedAddress, ...defaultAddressOptions } as AddressForm;
  };

  // TODO: update the default values, when a user goes all the way to the preview view
  //  and then goes back with the back button of the browser, selects the blank envelope
  //  and then goes back to the address view, the form values are not reset
  const formValues = useForm<AddressForm>({
    mode: 'onChange',
    delayError: 250,
    defaultValues: loadAddressFormData(), // todo: instead of assigning the default values, we should assign them from the context programatically
  });

  useEffect(() => {
    formValues.reset(loadAddressFormData());
  }, [isRecipientAddress]);

  return {
    formValues,
  };
};
